/**
 * RpgTransfersPage (ТЗ №5, «Переводы») — send mora / primogems to another player by
 * ``@username`` or Telegram id. A confirmation sheet («Вы уверены, что хотите отправить
 * N …?») is shown before the transfer is posted to ``POST /api/rpg/transfer/``; the
 * result (recipient + new sender balance) is then displayed. All validation (recipient
 * exists, positive amount, sufficient funds, no self-transfer) is enforced server-side.
 */
import { useState } from "react";

import { CurrencyIcon, Sheet } from "@melusine/shared-ui";
import { useBackButton } from "@melusine/shared-telegram";
import {
  CURRENCY_LABELS,
  useRpgProfile,
  type RpgTransferResult,
  type TransferCurrency,
} from "@entities/rpg";
import { rpgErrorMessage, useTransferMutation } from "@features/rpg";
import { StatePanel } from "@widgets/catalog-browser";
import { useToast } from "@shared/ui/toast";
import { formatUnits } from "@shared/lib/format";
import { routes, useNavigate } from "@shared/router";

import { RpgHeader } from "./ui/RpgHeader";
import styles from "./rpg.module.css";

export function RpgTransfersPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const profileQuery = useRpgProfile();
  const transfer = useTransferMutation();

  const [to, setTo] = useState("");
  const [currency, setCurrency] = useState<TransferCurrency>("mora");
  const [amountInput, setAmountInput] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<RpgTransferResult | null>(null);

  useBackButton(() => {
    navigate(routes.rpg());
  });

  if (profileQuery.isLoading) {
    return <StatePanel kind="loading" />;
  }
  if (profileQuery.data === null) {
    return (
      <StatePanel
        kind="error"
        message="Не удалось загрузить профиль."
        onRetry={profileQuery.refetch}
      />
    );
  }

  const profile = profileQuery.data;
  const balance = currency === "mora" ? profile.mora : profile.primogems;
  const amount = Number.parseInt(amountInput, 10);
  const validAmount = Number.isInteger(amount) && amount > 0;
  const hasRecipient = to.trim().length > 0;
  const affordable = validAmount && amount <= balance;
  const canSubmit = hasRecipient && validAmount && affordable && !transfer.isLoading;

  const onConfirm = async (): Promise<void> => {
    if (!canSubmit) {
      return;
    }
    try {
      const res = await transfer.mutate({
        to: to.trim(),
        currency,
        amount,
      });
      setResult(res);
      setConfirmOpen(false);
      setTo("");
      setAmountInput("");
      profileQuery.refetch();
      toast.show("Перевод отправлен.", "success");
    } catch (err) {
      setConfirmOpen(false);
      toast.show(rpgErrorMessage(err), "default");
    }
  };

  return (
    <div className={styles.page}>
      <RpgHeader
        title="Переводы"
        onBack={() => navigate(routes.rpg())}
        mora={profile.mora}
        primogems={profile.primogems}
      />

      <div className={styles.form}>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Получатель (@username или Telegram ID)</span>
          <input
            className={styles.input}
            type="text"
            autoCapitalize="none"
            autoCorrect="off"
            placeholder="@username"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>Валюта</span>
          <div className={styles.toggleRow}>
            {(["mora", "primogem"] as const).map((c) => (
              <button
                key={c}
                type="button"
                className={`${styles.toggle} ${currency === c ? styles.toggleActive : ""}`}
                onClick={() => setCurrency(c)}
              >
                <CurrencyIcon kind={c === "mora" ? "mora" : "primogem"} size={16} />
                {CURRENCY_LABELS[c]}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            Сумма (доступно {formatUnits(balance)})
          </span>
          <input
            className={styles.input}
            type="number"
            inputMode="numeric"
            min={1}
            placeholder="0"
            value={amountInput}
            onChange={(e) => setAmountInput(e.target.value)}
          />
        </div>

        {amountInput.trim().length > 0 && validAmount && !affordable ? (
          <div className={styles.previewBox}>
            <span style={{ color: "#e0a3a3" }}>
              Недостаточно средств (доступно {formatUnits(balance)}).
            </span>
          </div>
        ) : null}

        <button
          type="button"
          className={styles.primaryBtn}
          disabled={!canSubmit}
          onClick={() => setConfirmOpen(true)}
        >
          Отправить
        </button>

        {result ? (
          <div className={styles.resultBox}>
            Отправлено {formatUnits(result.amount)}{" "}
            {CURRENCY_LABELS[result.currency].toLowerCase()} игроку{" "}
            {result.recipient_label}. Ваш баланс: {formatUnits(result.sender_balance)}.
          </div>
        ) : null}
      </div>

      <Sheet
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Подтверждение перевода"
      >
        <div className={styles.confirmBody}>
          <p className={styles.confirmText}>
            Вы уверены, что хотите отправить{" "}
            <b>
              {formatUnits(validAmount ? amount : 0)}{" "}
              {CURRENCY_LABELS[currency].toLowerCase()}
            </b>{" "}
            игроку <b>{to.trim()}</b>?
          </p>
          <div className={styles.confirmActions}>
            <button
              type="button"
              className={styles.confirmCancel}
              onClick={() => setConfirmOpen(false)}
            >
              Отмена
            </button>
            <button
              type="button"
              className={styles.confirmConfirm}
              disabled={!canSubmit}
              onClick={() => void onConfirm()}
            >
              Отправить
            </button>
          </div>
        </div>
      </Sheet>
    </div>
  );
}
